import { useState } from "react";
import JSZip from "jszip";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import {
  fetchUploadLimits,
  getMaxFileCount,
  getSizeLimitBytes,
  getSizeLimitMb,
  isExtensionAllowed,
} from "../../../utils/uploadLimits";
import { getApprovalFileId } from "../utils/getApprovalFileId";
import {
  bulkMovePersonalItems,
  createPersonalSpaceFolder,
  createTrainedDocumentFolder,
  createGroupFolder,
  deletePersonalFile,
  deleteTrainedDocuments,
  deleteUnorganizedDoc,
  deleteUploadedFileFromPython,
  deleteContentFromPython,
  approveDocumentFromPython,
  rejectDocumentFromPython,
  deleteUserUploadRecord,
  downloadPersonalFile,
  downloadTrainedDocument,
  downloadGroupFile,
  markPersonalFileInactive,
  moveOrganizedItems,
  moveUnorganizedFiles,
  revertPersonalFile,
  renamePersonalSpaceItem,
  renameRepoItem,
  triggerBlobDownload,
  updateApprovalStatus,
  updateFileStatus,
  updateQaTextStatus,
  uploadForTraining,
  uploadPersonalFiles,
  downloadFileForTraining,
} from "../services/repoOrganizerService";
import {
  getRepoItemBackendId,
  getRepoMovePayloadItem,
} from "../utils/repoItemIdentity";

export function useRepoItemActions({
  items,
  setItems,
  fetchItems,
  role,
  repoView,
  currentFolder,
  groupId,
  createFolderModal,
  setCreateFolderModal,
  deleteModal,
  setDeleteModal,
  setSelectedItems,
}) {
  const [showFirstModal, setShowFirstModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState("Global");
  const [showUploadModeDropdown, setShowUploadModeDropdown] = useState(false);
  const [dataName, setDataName] = useState("");
  const [description, setDescription] = useState("");
  const [summaryDataList, setSummaryDataList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFileToTrain, setSelectedFileToTrain] = useState(null);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [isTraining, setIsTraining] = useState(false);
  const [updatingItems, setUpdatingItems] = useState(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameText, setRenameText] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);
  const isGroupRepoView = repoView === "group";

  const isGroupRootItem = (item) => Boolean(item?.isGroupRoot);

  // Text/Q&A items live in the UserUpload table — their only status is
  // upload_status ("1" = Approved, "0" = Rejected; no pending/duplicate).
  const isTextQaFile = (item) =>
    item?.type === "file" &&
    (item?.fileType === "text" || item?.fileType === "qa");

  const collectPersonalTreeItemIds = (rootItem, sourceItems = items) => {
    if (!rootItem?.id) return [];

    const rootId = String(rootItem.id);
    const ids = new Set([rootId]);
    const stack = [rootId];

    while (stack.length > 0) {
      const currentParentId = stack.pop();
      sourceItems.forEach((candidate) => {
        if (String(candidate?.parentId ?? "") !== currentParentId) return;
        if (ids.has(String(candidate.id))) return;
        ids.add(String(candidate.id));
        if (candidate.type === "folder") {
          stack.push(String(candidate.id));
        }
      });
    }

    return Array.from(ids);
  };

  // ── Delete flow helpers ────────────────────────────────────────────────
  // Reads the current user + enterprise identifiers from persisted storage.
  const getDeleteUserContext = () => {
    let user = null;
    try {
      user =
        JSON.parse(localStorage.getItem("user") || "null") ||
        JSON.parse(sessionStorage.getItem("user") || "null") ||
        null;
    } catch {
      user = null;
    }

    const enterpriseId =
      localStorage.getItem("enterprise_id") ||
      sessionStorage.getItem("enterprise_id") ||
      user?.enterprise_id ||
      user?.enterprise_fid ||
      user?.enterpriseId ||
      null;

    const userId =
      user?.id ?? user?.user_id ?? user?.userId ?? user?.user_fid ?? null;

    return {
      enterprise_id: enterpriseId == null ? null : String(enterpriseId),
      user_id: userId == null ? null : String(userId),
    };
  };

  // Recursively collects every file item (including descendants of folders)
  // from the delete selection so Python vectors can be cleaned up for all of them.
  const collectDeletableFileItems = (rootItems, sourceItems = items) => {
    const files = [];
    const visited = new Set();

    const walk = (item) => {
      if (!item || visited.has(String(item.id))) return;
      visited.add(String(item.id));

      if (item.type === "folder") {
        sourceItems
          .filter((c) => String(c.parentId) === String(item.id))
          .forEach(walk);
        return;
      }

      if (item.type === "file") files.push(item);
    };

    rootItems.forEach(walk);
    return files;
  };

  // Resolves the identifier Python needs for a file item.
  const getPythonDocumentId = (item) =>
    item?.document_id || item?.documentId || null;

  const getPythonFileUuid = (item) =>
    item?.pythonFileUuid || item?.python_file_uuid || null;

  const getUploadedFileId = (item) =>
    item?.uploaded_file_id || item?.file_id || null;

  // Maps a repo item to the Python /content-delete/ content_type when it is
  // web/video/text/Q&A content (as opposed to a regular document). Returns null
  // for regular documents, which use the /delete_uploaded_file/ path instead.
  const getContentType = (item) => {
    const fileType = String(item?.fileType || "").toLowerCase();
    const ext = String(item?.fileExtension || "").toLowerCase();
    const name = String(item?.name || "").toLowerCase();
    if (fileType === "text") return "text";
    if (fileType === "qa") return "qa";
    if (fileType === "webpage" || fileType === "url" || ext === "webpage")
      return "web_url";
    if (
      ["youtube_audio", "youtube", "video"].includes(fileType) ||
      ext === "youtube" ||
      name.includes("youtube.com") ||
      name.includes("youtu.be")
    )
      return "video";
    // Trained web/video items carry the URL as their name (no fileType) —
    // same heuristic filterRepoItems uses for the URL filter.
    if (/^https?:\/\//.test(name)) return "web_url";
    return null;
  };

  // Calls Python POST /delete_uploaded_file/ for every file in the selection.
  // Throws if any call fails — callers must NOT delete PG records or physical
  // files unless this resolves successfully.
  const deleteVectorsFromPython = async (itemsToDelete) => {
    const fileItems = collectDeletableFileItems(itemsToDelete);
    const { enterprise_id, user_id } = getDeleteUserContext();

    const targets = [];
    const seen = new Set();

    fileItems.forEach((item) => {
      const content_type = getContentType(item);
      const document_id = getPythonDocumentId(item);
      const python_file_uuid = getPythonFileUuid(item);
      const uploaded_file_id = getUploadedFileId(item);
      const user_files_id = item?.isPersonalSpace
        ? item?.originalId || item?.backendId || null
        : null;

      // Web/video/text/Q&A items are deleted from Pinecone by document_id via
      // Python /content-delete/. My Drive items carry no document_id on the
      // frontend — the backend resolves it from the linked UploadedFile.
      const key = content_type
        ? document_id
        : python_file_uuid || uploaded_file_id || document_id || user_files_id;
      if (!key || seen.has(key)) return;
      seen.add(key);

      targets.push({
        document_id,
        python_file_uuid,
        uploaded_file_id,
        user_files_id,
        mode: item?.mode || null,
        content_type,
      });
    });

    if (targets.length === 0) return;

    await Promise.all(
      targets.map(
        ({
          document_id,
          python_file_uuid,
          uploaded_file_id,
          user_files_id,
          mode,
          content_type,
        }) =>
          content_type
            ? deleteContentFromPython({
                document_id,
                enterprise_id,
                content_type,
              })
            : deleteUploadedFileFromPython({
                document_id,
                python_file_uuid,
                uploaded_file_id,
                user_files_id,
                enterprise_id,
                user_id,
                ...(mode ? { mode } : {}),
              }),
      ),
    );
  };

  // Calls Python POST /approve_document or /reject_document for a content item
  // (text/Q&A) so the vectors are promoted/rejected in Pinecone. Uses the same
  // enterprise/user context as the delete flow. Throws if document_id is
  // missing — callers must NOT update the DB status unless Python succeeds.
  const callPythonApproval = async (item, newStatus) => {
    const documentId = getPythonDocumentId(item);
    if (!documentId) {
      throw new Error(
        "document_id missing — cannot update vectors in Python for this item",
      );
    }

    const { enterprise_id, user_id } = getDeleteUserContext();

    if (newStatus === "approved") {
      await approveDocumentFromPython({
        document_id: documentId,
        enterprise_id,
        approved_by: user_id,
      });
    } else if (newStatus === "rejected") {
      await rejectDocumentFromPython({
        document_id: documentId,
        enterprise_id,
        rejected_by: user_id,
        reason: "",
      });
    }
  };

  const isSameGroup = (left, right) =>
    String(left ?? "") === String(right ?? "");

  const handleNavigate = (
    folderId,
    folderName,
    setCurrentFolder,
    setBreadcrumbs,
  ) => {
    const safeFolderId = folderId ? String(folderId) : null;
    setCurrentFolder(safeFolderId);
    setSelectedItems(new Set());
    if (safeFolderId === null) {
      setBreadcrumbs([{ id: null, name: "Root" }]);
    } else {
      setBreadcrumbs((prev) => {
        const index = prev.findIndex((b) => b.id === safeFolderId);
        if (index !== -1) return prev.slice(0, index + 1);
        return [...prev, { id: safeFolderId, name: folderName }];
      });
    }
  };

  const handleCreateFolder = async () => {
    const folderName = createFolderModal.name.trim();
    if (!folderName) {
      showErrorToast("Folder name cannot be empty");
      return false;
    }

    const isPS = repoView === "personal_space";
    if (isGroupRepoView && role !== "admin") {
      showErrorToast("Only admins can create folders inside a group.");
      return false;
    }

    if (isGroupRepoView && !groupId) {
      showErrorToast("Select a group before creating a folder.");
      return false;
    }

    const duplicateExists = items.some((item) => {
      if (item.type !== "folder") return false;
      if (item.parentId !== currentFolder) return false;
      if (item.name?.trim().toLowerCase() !== folderName.toLowerCase())
        return false;
      if (isPS) return item.isPersonalSpace === true;
      if (isGroupRepoView) return item.groupId === groupId;
      if (repoView === "global")
        return (
          item.ownerType === "admin" ||
          item.mode === "global" ||
          item.mode === "group"
        );
      return (
        item.ownerType !== "admin" &&
        item.mode !== "global" &&
        item.mode !== "group"
      );
    });

    if (duplicateExists) {
      showErrorToast("Folder with this name already exists");
      return false;
    }

    try {
      let createdFolder;

      if (isGroupRepoView) {
        // Create a sub-folder inside a group
        const res = await createGroupFolder(groupId, folderName, currentFolder);
        const createdFolderId = res.data?.data?.id;
        if (!createdFolderId) throw new Error("Failed to create folder");

        createdFolder = {
          id: String(createdFolderId),
          name: folderName,
          type: "folder",
          parentId: currentFolder,
          size: 0,
          updatedAt: new Date().toISOString(),
          uploadedBy: "Me",
          isPersonalSpace: false,
          ownerType: "admin",
          mode: "group",
          groupId: String(groupId),
          isUnorganized: false,
          approvalStatus: "approved",
          fileStatus: "1",
          createdAt: new Date().toISOString(),
        };
      } else if (isPS) {
        const res = await createPersonalSpaceFolder(folderName, currentFolder);
        const createdFolderId = res.data?.folder?.user_files_id;
        if (!createdFolderId) throw new Error("Failed to create folder");

        createdFolder = {
          id: `ps-folder-${createdFolderId}`,
          name: folderName,
          type: "folder",
          parentId: currentFolder,
          size: 0,
          updatedAt: new Date().toISOString(),
          uploadedBy: "Me",
          isPersonalSpace: true,
          ownerType: "user",
          mode: "personal_space",
          isUnorganized: false,
          approvalStatus: "approved",
          fileStatus: "1",
          createdAt: new Date().toISOString(),
        };
      } else {
        const res = await createTrainedDocumentFolder(
          folderName,
          currentFolder,
        );
        const createdFolderId = res.data?.data?.id;
        if (!createdFolderId) throw new Error("Failed to create folder");

        createdFolder = {
          id: String(createdFolderId),
          name: folderName,
          type: "folder",
          parentId: currentFolder,
          size: 0,
          updatedAt: new Date().toISOString(),
          uploadedBy: "Me",
          isPersonalSpace: false,
          ownerType: role === "admin" ? "admin" : "user",
          mode: role === "admin" ? "global" : "personal",
          isUnorganized: false,
          approvalStatus: "approved",
          fileStatus: "1",
          createdAt: new Date().toISOString(),
        };
      }

      setItems((prev) => [...prev, createdFolder]);
      setCreateFolderModal({ show: false, name: "" });
      showSuccessToast("Folder created successfully");
      return true;
    } catch (err) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || "Failed to create folder";
      showErrorToast(errorMessage);
      return false;
    }
  };

  const handleDelete = async (strategy = "DELETE") => {
    const itemsToDelete = deleteModal.items;
    const deletedItemIds = new Set(itemsToDelete.map((i) => i.id));
    // Text/Q&A rows live in user_uploads, not trained_documents — they are
    // deleted separately below after their Pinecone chunks are removed.
    const textQaItems = itemsToDelete.filter(
      (i) =>
        i.type === "file" &&
        (i.fileType === "text" || i.fileType === "qa"),
    );
    const organizedIds = itemsToDelete
      .filter(
        (i) =>
          !textQaItems.includes(i) &&
          !i.id.toString().startsWith("unorganized-"),
      )
      .map((i) => getRepoItemBackendId(i));
    const unorganizedIds = itemsToDelete
      .filter((i) => i.id.toString().startsWith("unorganized-"))
      .map((i) => i.id.replace("unorganized-", ""));
    const personalSpaceItems =
      itemsToDelete.length > 0 && itemsToDelete.every((i) => i.isPersonalSpace)
        ? itemsToDelete
        : [];

    try {
      if (personalSpaceItems.length > 0) {
        const softDeleteItems = personalSpaceItems.filter(
          (item) => String(item.fileStatus) === "1",
        );
        const hardDeleteItems = personalSpaceItems.filter(
          (item) => String(item.fileStatus) !== "1",
        );

        // 🔒 My Drive permanent delete: Python FIRST. If the Python endpoint
        // fails, we must NOT remove DB records or physical files.
        if (hardDeleteItems.length > 0) {
          await deleteVectorsFromPython(hardDeleteItems);
        }

        await Promise.all([
          ...softDeleteItems.map((item) =>
            markPersonalFileInactive(item.originalId),
          ),
          ...hardDeleteItems.map((item) => deletePersonalFile(item.originalId)),
        ]);

        setItems((prev) => prev.filter((item) => !deletedItemIds.has(item.id)));
        setDeleteModal({ show: false, items: [] });
        setSelectedItems(new Set());
        showSuccessToast(
          softDeleteItems.length > 0 && hardDeleteItems.length > 0
            ? `Moved ${softDeleteItems.length} item(s) to Deleted and permanently deleted ${hardDeleteItems.length} item(s)`
            : softDeleteItems.length > 0
              ? "Moved to Deleted"
              : "Permanently deleted",
        );
        return;
      }

      // 🔒 Repo delete (single file / folder / multiple files): call Python
      // POST /delete_uploaded_file/ FIRST. Only on success do we delete the
      // PostgreSQL records and the physical files below.
      await deleteVectorsFromPython(itemsToDelete);

      await Promise.all([
        organizedIds.length > 0
          ? deleteTrainedDocuments(organizedIds, strategy)
          : Promise.resolve(),
        ...unorganizedIds.map((fileId) => deleteUnorganizedDoc(fileId)),
        ...textQaItems.map((item) =>
          deleteUserUploadRecord(getRepoItemBackendId(item)),
        ),
      ]);

      const deletedIds = new Set(itemsToDelete.map((i) => i.id));
      setItems((prev) => prev.filter((item) => !deletedIds.has(item.id)));
      setDeleteModal({ show: false, items: [] });
      setSelectedItems(new Set());

      showSuccessToast(
        itemsToDelete.length > 1
          ? `Deleted ${itemsToDelete.length} items`
          : "Item deleted",
      );
    } catch (err) {
      console.error("Delete error:", err);
      showErrorToast(
        err?.response?.data?.error || err?.message || "Delete failed",
      );
      fetchItems();
    }
  };

  const fetchItemDownloadBlob = async (item) => {
    if (item.type === "folder") return null;

    if (item.mode === "group" || item.groupId) {
      const res = await downloadGroupFile(item);
      return res.data;
    }

    if (item.isPersonalSpace) {
      const res = await downloadPersonalFile(item.originalId);
      return res.data;
    }

    const idToUse = item.uploaded_file_id || item.id;
    const res = await downloadTrainedDocument(idToUse);
    return res.data;
  };

  const handleDownload = async (item) => {
    try {
      const blob = await fetchItemDownloadBlob(item);
      if (blob) {
        triggerBlobDownload(blob, item.name);
      }
      return blob;
    } catch (err) {
      console.error("Download failed:", err);
      if (err.response && err.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            showErrorToast(errorData.message || "Download failed");
          } catch (e) {
            showErrorToast("Download failed");
          }
        };
        reader.readAsText(err.response.data);
      } else {
        showErrorToast(err.response?.data?.message || "Download failed");
      }
      return null;
    }
  };

  const handleBulkDelete = async (selectedIds) => {
    const itemsToDelete = items.filter((i) => selectedIds.has(i.id));
    const deletedItemIds = new Set(itemsToDelete.map((i) => i.id));
    // Text/Q&A rows live in user_uploads, not trained_documents — they are
    // deleted separately below after their Pinecone chunks are removed.
    const textQaItems = itemsToDelete.filter(
      (i) =>
        i.type === "file" &&
        (i.fileType === "text" || i.fileType === "qa"),
    );
    const organizedIds = itemsToDelete
      .filter((i) => !i.isUnorganized && !textQaItems.includes(i))
      .map((i) => getRepoItemBackendId(i));
    const unorganizedIds = itemsToDelete
      .filter((i) => i.isUnorganized)
      .map((i) => i.id.replace("unorganized-", ""));
    const personalSpaceItems =
      itemsToDelete.length > 0 && itemsToDelete.every((i) => i.isPersonalSpace)
        ? itemsToDelete
        : [];

    try {
      if (personalSpaceItems.length > 0) {
        const softDeleteItems = personalSpaceItems.filter(
          (item) => String(item.fileStatus) === "1",
        );
        const hardDeleteItems = personalSpaceItems.filter(
          (item) => String(item.fileStatus) !== "1",
        );

        // 🔒 My Drive permanent delete: Python FIRST. If the Python endpoint
        // fails, we must NOT remove DB records or physical files.
        if (hardDeleteItems.length > 0) {
          await deleteVectorsFromPython(hardDeleteItems);
        }

        await Promise.all([
          ...softDeleteItems.map((item) =>
            markPersonalFileInactive(item.originalId),
          ),
          ...hardDeleteItems.map((item) => deletePersonalFile(item.originalId)),
        ]);

        setItems((prev) => prev.filter((item) => !deletedItemIds.has(item.id)));
        setSelectedItems(new Set());
        showSuccessToast(
          softDeleteItems.length > 0 && hardDeleteItems.length > 0
            ? `Moved ${softDeleteItems.length} item(s) to Deleted and permanently deleted ${hardDeleteItems.length} item(s)`
            : softDeleteItems.length > 0
              ? `Moved ${softDeleteItems.length} item(s) to Deleted`
              : `Permanently deleted ${hardDeleteItems.length} item(s)`,
        );
        return;
      }

      // 🔒 Bulk repo delete (single file / folder / multiple files): call Python
      // POST /delete_uploaded_file/ FIRST. Only on success do we delete the
      // PostgreSQL records and the physical files below.
      await deleteVectorsFromPython(itemsToDelete);

      await Promise.all([
        organizedIds.length > 0
          ? deleteTrainedDocuments(organizedIds, "DELETE")
          : Promise.resolve(),
        ...unorganizedIds.map((fileId) => deleteUnorganizedDoc(fileId)),
        ...textQaItems.map((item) =>
          deleteUserUploadRecord(getRepoItemBackendId(item)),
        ),
      ]);

      setItems((prev) => prev.filter((item) => !selectedIds.has(item.id)));
      setSelectedItems(new Set());
      showSuccessToast(`Deleted ${itemsToDelete.length} items`);
    } catch (err) {
      console.error("Bulk delete error:", err);
      showErrorToast(
        err?.response?.data?.error || err?.message || "Bulk delete failed",
      );
      fetchItems();
    }
  };

  const handleMove = async (itemsToMove, targetFolderId) => {
    // ── Group Repository: enforce group isolation ──────────────────────────
    if (isGroupRepoView) {
      if (role !== "admin") {
        showErrorToast("Only admins can move items inside a group.");
        return;
      }

      if (!targetFolderId) {
        showErrorToast("Choose a destination folder inside the same group.");
        return;
      }

      // Determine the target folder's groupId from our items list.
      const targetFolder = items.find((i) => i.id === targetFolderId);
      if (!targetFolder || isGroupRootItem(targetFolder)) {
        showErrorToast("Choose a destination folder inside the same group.");
        return;
      }

      const targetGroupId = targetFolder?.groupId;
      const sourceGroupId = itemsToMove[0]?.groupId || null;

      if (!sourceGroupId || !isSameGroup(sourceGroupId, targetGroupId)) {
        showErrorToast("Files and folders cannot be moved across groups.");
        return;
      }

      // Block if any item's groupId differs from the target folder's groupId
      const crossGroupItems = itemsToMove.filter(
        (i) => i.groupId && !isSameGroup(i.groupId, targetGroupId),
      );
      if (crossGroupItems.length > 0) {
        showErrorToast("Files and folders cannot be moved across groups.");
        return;
      }

      // Also block if an item is a group root folder (isGroupRoot)
      const rootFolderItems = itemsToMove.filter(isGroupRootItem);
      if (rootFolderItems.length > 0) {
        showErrorToast("Group folders cannot be moved.");
        return;
      }
    }

    if (repoView === "personal_space") {
      try {
        const targetParentId = targetFolderId
          ? targetFolderId.replace("ps-folder-", "")
          : null;
        const itemIds = itemsToMove.map((item) => item.originalId);

        const movedItemIds = new Set(itemsToMove.map((i) => i.id));
        setItems((prev) =>
          prev.map((item) =>
            movedItemIds.has(item.id)
              ? { ...item, parentId: targetFolderId }
              : item,
          ),
        );

        setSelectedItems(new Set());
        showSuccessToast(`Moved ${itemsToMove.length} items`);

        await bulkMovePersonalItems(itemIds, targetParentId);
      } catch (err) {
        console.error("Move error:", err);
        showErrorToast(err?.response?.data?.message || "Move failed");
        fetchItems();
      }
      return;
    }

    const unorganizedIds = itemsToMove
      .filter((i) => i.isUnorganized)
      .map((i) => getRepoItemBackendId(i));
    const unsupportedMoveItems = itemsToMove.filter(
      (item) => !item.isUnorganized && !getRepoMovePayloadItem(item),
    );
    if (unsupportedMoveItems.length > 0) {
      showErrorToast("Some selected items cannot be moved");
      return;
    }
    const organizedItems = itemsToMove 
      .filter((i) => !i.isUnorganized)
      .map(getRepoMovePayloadItem);

    try {
      const movedItemIds = new Set(itemsToMove.map((i) => i.id));
      setItems((prev) =>
        prev.map((item) =>
          movedItemIds.has(item.id)
            ? { ...item, parentId: targetFolderId }
            : item,
        ),
      );

      setSelectedItems(new Set());
      showSuccessToast(`Moved ${itemsToMove.length} items`);

      await Promise.all([
        unorganizedIds.length > 0
          ? moveUnorganizedFiles(
              unorganizedIds,
              targetFolderId,
              isGroupRepoView ? groupId : null,
            )
          : Promise.resolve(),
        organizedItems.length > 0
          ? moveOrganizedItems(
              organizedItems,
              targetFolderId,
              isGroupRepoView ? groupId : null,
            )
          : Promise.resolve(),
      ]);
    } catch (err) {
      console.error("Move error:", err);
      showErrorToast(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Move failed",
      );
      fetchItems();
    }
  };

  const toggleSelect = (id, setSelectedItemsState) => {
    setSelectedItemsState((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleChangeFileStatus = async (item) => {
    if (item.isPersonalSpace) {
      try {
        if (item.fileStatus === "0") {
          if (updatingItems.has(item.id)) return;
          setUpdatingItems((prev) => new Set(prev).add(item.id));
          await revertPersonalFile(item.originalId);
          const restoredIds = new Set(
            collectPersonalTreeItemIds(item),
          );
          setItems((prev) =>
            prev.filter((candidate) => !restoredIds.has(String(candidate.id))),
          );
          showSuccessToast("Restored to Saved");
        }
      } catch (err) {
        console.error("Error restoring file:", err);
        showErrorToast(err?.response?.data?.message || "Something went wrong");
      } finally {
        if (item.fileStatus === "0") {
          setUpdatingItems((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
        }
      }
      return;
    }

    console.log("Change status item:", item);

    const fileId =
      item.pythonFileUuid || item.uploaded_file_id || item.originalId;

    if (!fileId) {
      showErrorToast("File identifier missing");
      return;
    }

    if (updatingItems.has(item.id)) return;

    try {
      setUpdatingItems((prev) => new Set(prev).add(item.id));
      const newStatus = item.fileStatus === "1" ? "0" : "1";

      await updateFileStatus(fileId, item.fileType, newStatus);
      
      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id ? { ...i, fileStatus: newStatus } : i,
        ),
      );
      showSuccessToast("Status updated successfully");
    } catch (err) {
      console.error("Error updating file status:", err);
      showErrorToast(err?.response?.data?.message || "Something went wrong");
    } finally {
      setUpdatingItems((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleBulkRestore = async (selectedItems) => {
    const personalDeletedItems = selectedItems.filter(
      (item) => item.isPersonalSpace && String(item.fileStatus) === "0",
    );

    if (personalDeletedItems.length === 0) return;

    try {
      setIsBulkUpdating(true);

      await Promise.all(
        personalDeletedItems.map((item) => revertPersonalFile(item.originalId)),
      );

      const restoredIds = new Set();
      personalDeletedItems.forEach((item) => {
        collectPersonalTreeItemIds(item).forEach((id) => restoredIds.add(id));
      });

      setItems((prev) =>
        prev.filter((candidate) => !restoredIds.has(String(candidate.id))),
      );
      setSelectedItems(new Set());
      showSuccessToast(
        personalDeletedItems.length > 1
          ? `Restored ${personalDeletedItems.length} items to Saved`
          : "Restored to Saved",
      );
    } catch (err) {
      console.error("Bulk restore failed:", err);
      showErrorToast(err?.response?.data?.message || "Bulk restore failed");
      fetchItems();
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleChangeApprovalStatus = async (itemId, newStatus) => {
    if (updatingItems.has(itemId)) return;

    const item = items.find((i) => i.id === itemId) || { id: itemId };
    const approvalFileId = getApprovalFileId(item);

    try {
      setUpdatingItems((prev) => new Set(prev).add(itemId));

      if (isTextQaFile(item)) {
        // Text/Q&A use upload_status: "1" = approved, "0" = rejected.
        // Python first (promote/reject vectors), then the DB status update.
        await callPythonApproval(item, newStatus);

        const uploadStatus = newStatus === "approved" ? "1" : "0";
        const fileId =
          item.pythonFileUuid || item.backendId || approvalFileId;
        await updateFileStatus(fileId, item.fileType, uploadStatus);

        setItems((prev) =>
          prev.map((i) =>
            i.id === itemId
              ? {
                  ...i,
                  approvalStatus: newStatus,
                  fileStatus: uploadStatus,
                }
              : i,
          ),
        );
      } else {
        await updateApprovalStatus(approvalFileId, newStatus);

        setItems((prev) =>
          prev.map((item) =>
            item.id === itemId
              ? { ...item, approvalStatus: newStatus }
              : item,
          ),
        );
      }

      showSuccessToast(`Doc status set to ${newStatus}`);
    } catch (err) {
      console.error("Error updating approval status:", err);
      showErrorToast(err?.response?.data?.message || "Something went wrong");
    } finally {
      setUpdatingItems((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const handleBulkApprovalStatus = async (newStatus, selectedItems) => {
    if (isBulkUpdating) return;
    const fileItems = items.filter(
      (i) => selectedItems.has(i.id) && i.type === "file",
    );
    if (fileItems.length === 0) {
      showErrorToast("No files selected");
      return;
    }
    try {
      setIsBulkUpdating(true);
      await Promise.all(
        fileItems.map(async (f) => {
          if (isTextQaFile(f)) {
            // Text/Q&A can't be "pending" — skip, or map approved/rejected
            // to upload_status "1"/"0". Python first, then the DB status.
            if (newStatus === "pending") return Promise.resolve();
            await callPythonApproval(f, newStatus);
            const uploadStatus = newStatus === "approved" ? "1" : "0";
            const fileId = f.pythonFileUuid || f.backendId || getApprovalFileId(f);
            return updateFileStatus(fileId, f.fileType, uploadStatus);
          }
          return updateApprovalStatus(getApprovalFileId(f), newStatus);
        }),
      );

      const updateIds = new Set(fileItems.map((i) => i.id));
      setItems((prev) =>
        prev.map((item) => {
          if (!updateIds.has(item.id)) return item;
          if (isTextQaFile(item) && newStatus === "pending") return item;
          const next = { ...item, approvalStatus: newStatus };
          if (isTextQaFile(item)) {
            next.fileStatus = newStatus === "approved" ? "1" : "0";
          }
          return next;
        }),
      );
      setSelectedItems(new Set());

      showSuccessToast(`${fileItems.length} file(s) set to ${newStatus}`);
    } catch (err) {
      console.error("Bulk approval error:", err);
      showErrorToast("Failed to update one or more files");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleBulkDownload = async (selectedIds) => {
    const selectedList = items.filter((i) => selectedIds.has(i.id));
    if (selectedList.length === 0) {
      showErrorToast("No items selected for download");
      return;
    }

    const downloadableFiles = [];
    const visitedIds = new Set();

    const walk = (node, pathSegments = []) => {
      if (!node) return;

      if (node.type === "folder") {
        const nextPath = [...pathSegments, node.name];
        items
          .filter((child) => String(child.parentId) === String(node.id))
          .forEach((child) => walk(child, nextPath));
        return;
      }

      if (node.type !== "file") return;
      if (String(node.fileType || "").toLowerCase() === "text") return;
      if (String(node.fileType || "").toLowerCase() === "qa") return;
      if (["webpage", "youtube_audio", "url", "video"].includes(String(node.fileType || "").toLowerCase())) return;

      const uniqueKey = String(node.id);
      if (visitedIds.has(uniqueKey)) return;
      visitedIds.add(uniqueKey);
      downloadableFiles.push({
        item: node,
        entryPath: [...pathSegments, node.name].join("/"),
      });
    };

    selectedList.forEach((item) => {
      if (item.type === "folder") {
        walk(item, []);
        return;
      }

      walk(item, []);
    });

    if (downloadableFiles.length === 0) {
      showErrorToast("No downloadable files selected");
      return;
    }

    showSuccessToast(`Creating ZIP archive with ${downloadableFiles.length} file(s)...`);

    try {
      const zip = new JSZip();

      for (const fileEntry of downloadableFiles) {
        const blob = await fetchItemDownloadBlob(fileEntry.item);
        if (blob) {
          zip.file(fileEntry.entryPath, blob, { createFolders: true });
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      triggerBlobDownload(zipBlob, "repo-download.zip");
      showSuccessToast(`${downloadableFiles.length} file(s) downloaded as ZIP`);
    } catch (err) {
      console.error("Bulk download failed:", err);
      showErrorToast("Failed to create ZIP archive");
    }
  };

  const handleProcessFile = (file) => {
    setSelectedFileToTrain(file);
    setShowFirstModal(true);
  };

  const openRenameModal = (item) => {
    setRenameTarget(item);
    setRenameText(item.name || "");
    setShowRenameModal(true);
  };

  const closeRenameModal = () => {
    setShowRenameModal(false);
    setRenameTarget(null);
    setRenameText("");
    setIsRenaming(false);
  };

  const getRenamedItemDisplayName = (item, responseData, fallbackName) => {
    if (item?.isPersonalSpace) {
      return (
        responseData?.item?.user_files_name ||
        responseData?.item?.name ||
        fallbackName
      );
    }

    const updatedItem = responseData?.updatedItem;
    if (!updatedItem) {
      return fallbackName;
    }

    if (item?.itemSource === "user_upload") {
      return updatedItem.text_title || updatedItem.question || fallbackName;
    }

    if (item?.itemSource === "uploaded_file") {
      return (
        updatedItem.original_filename ||
        updatedItem.filename ||
        fallbackName
      );
    }

    return updatedItem.name || fallbackName;
  };

  const performRename = async () => {
    if (!renameTarget) {
      showErrorToast("No item selected for rename");
      return;
    }

    const newName = renameText.trim();
    if (!newName) {
      showErrorToast("Name cannot be empty");
      return;
    }

    const duplicateExists = items.some((item) => {
      if (item.parentId !== renameTarget.parentId) return false;
      if (item.id === renameTarget.id) return false;
      return item.name?.trim().toLowerCase() === newName.toLowerCase();
    });

    if (duplicateExists) {
      showErrorToast("An item with this name already exists");
      return;
    }

    try {
      setIsRenaming(true);
      let response;
      if (renameTarget.isPersonalSpace) {
        response = await renamePersonalSpaceItem(
          getRepoItemBackendId(renameTarget),
          newName,
        );
      } else {
        response = await renameRepoItem(renameTarget, newName);
      }

      const updatedName = getRenamedItemDisplayName(
        renameTarget,
        response?.data,
        newName,
      );

      setItems((prev) =>
        prev.map((item) =>
          item.id === renameTarget.id ? { ...item, name: updatedName } : item,
        ),
      );

      showSuccessToast("Item renamed successfully");
      closeRenameModal();
    } catch (err) {
      console.error("❌ Rename failed", err);
      showErrorToast(err?.response?.data?.message || err?.response?.data?.error || "Rename failed");
      setIsRenaming(false);
    }
  };

  const handleAction = (action, item, setMoveModalState) => {
    if (isGroupRepoView && role !== "admin" && action !== "download") {
      showErrorToast("Only admins can manage items inside a group.");
      return;
    }

    if (isGroupRootItem(item) && action !== "download") {
      showErrorToast("Group folders cannot be modified.");
      return;
    }

    switch (action) {
      case "download":
        handleDownload(item);
        break;
      case "status":
        handleChangeFileStatus(item);
        break;
      case "process":
        handleProcessFile(item);
        break;
      case "revert":
        handleChangeFileStatus(item);
        break;
      case "delete":
        setDeleteModal({ show: true, items: [item] });
        break;
      case "move":
        setMoveModalState({ show: true, items: [item], targetFolderId: null });
        break;
      case "rename":
        openRenameModal(item);
        break;
      default:
        break;
    }
  };

  const handleSendForTraining = async (file, mode) => {
    try {
      setIsTraining(true);
      setShowTrainingModal(false);
      showSuccessToast(`Sending ${file.name} for training...`);

      const downloadRes = await downloadFileForTraining(file.originalId);
      const blob = downloadRes.data;
      const selectedFile = new File([blob], file.name, {
        type: file.fileType || blob.type || "application/octet-stream",
      });

      const formData = new FormData();
      formData.append("files", selectedFile);
      formData.append("mode", mode.toLowerCase());
      formData.append("model", "llama-3.3-70b-versatile");

      const response = await uploadForTraining(formData);

      if (response.status === 200 || response.status === 207) {
        showSuccessToast(`${file.name} trained successfully!`);
        await markPersonalFileInactive(file.originalId);
        fetchItems();
      }
    } catch (err) {
      console.error("Training failed:", err);
      if (err.response && err.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorData = JSON.parse(reader.result);
            showErrorToast(errorData.message || "Training failed");
          } catch (e) {
            showErrorToast("Training failed");
          }
        };
        reader.readAsText(err.response.data);
      } else {
        showErrorToast(err.response?.data?.message || "Training failed");
      }
    } finally {
      setIsTraining(false);
      setSelectedFileToTrain(null);
    }
  };

  const uploadToBackend = async () => {
    try {
      setIsSubmitting(true);
      const firstItem = summaryDataList[0];
      if (firstItem && firstItem.originalId) {
        await markPersonalFileInactive(firstItem.originalId);
      }

      setShowSummaryModal(false);
      setSummaryDataList([]);
      fetchItems();
      showSuccessToast("Training completed!");
    } catch (err) {
      console.error("Submission failed:", err);
      showErrorToast("Submission failed!");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = async (e, setIsLoading) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Enterprise-configured upload limits (max count + max size + allowed extensions)
    const limits = await fetchUploadLimits();
    const maxCount = getMaxFileCount(limits);

    if (files.length > maxCount) {
      showErrorToast(
        `You can upload a maximum of ${maxCount} files at a time.`,
      );
      e.target.value = "";
      return;
    }

    // ✅ Enterprise-configurable allowed extensions (checkbox list + custom)
    const unsupported = files.filter((f) => !isExtensionAllowed(f.name, limits));
    if (unsupported.length > 0) {
      const names = unsupported.map((f) => f.name).join(", ");
      showErrorToast(`Unsupported file(s): ${names}`);
      e.target.value = "";
      return;
    }

    const oversized = files.filter(
      (f) => f.size > getSizeLimitBytes(f.name, limits),
    );
    if (oversized.length > 0) {
      oversized.forEach((f) => {
        const sizeMB = (f.size / (1024 * 1024)).toFixed(1);
        showErrorToast(
          `"${f.name}" (${sizeMB} MB) exceeds the ${getSizeLimitMb(
            f.name,
            limits,
          )} MB size limit.`,
        );
      });
      e.target.value = "";
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));

      await uploadPersonalFiles(formData);
      showSuccessToast("Files uploaded successfully");
      fetchItems();
    } catch (error) {
      console.error("Upload failed:", error);
      showErrorToast("Upload failed");
    } finally {
      setIsLoading(false);
      e.target.value = "";
    }
  };

  return {
    showFirstModal,
    setShowFirstModal,
    showSummaryModal,
    setShowSummaryModal,
    uploadedFiles,
    setUploadedFiles,
    uploadMode,
    setUploadMode,
    showUploadModeDropdown,
    setShowUploadModeDropdown,
    dataName,
    setDataName,
    description,
    setDescription,
    summaryDataList,
    setSummaryDataList,
    isSubmitting,
    selectedFileToTrain,
    showTrainingModal,
    setShowTrainingModal,
    isTraining,
    updatingItems,
    isBulkUpdating,
    handleNavigate,
    handleCreateFolder,
    handleDelete,
    handleBulkRestore,
    handleDownload,
    handleBulkDelete,
    handleMove,
    toggleSelect,
    handleChangeFileStatus,
    handleChangeApprovalStatus,
    handleBulkApprovalStatus,
    handleBulkDownload,
    handleProcessFile,
    handleAction,
    handleSendForTraining,
    uploadToBackend,
    handleFileChange,
    showRenameModal,
    renameTarget,
    renameText,
    setRenameText,
    isRenaming,
    closeRenameModal,
    performRename,
  };
}
